import React, { useState, useEffect } from 'react';
import { Home, PieChart, ArrowRightLeft, User, Shield, LogOut, X, PlusCircle, MinusCircle, CheckCircle2, XCircle, Landmark } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import logo from '../../assets/AlgoLogo.png';
import QR from '../../assets/qr.jpeg';

interface AccountSidebarProps {
  onLogout: () => void;
  isOpen: boolean;
  onClose: () => void;
}

const AccountSidebar = ({ onLogout, isOpen, onClose }: AccountSidebarProps) => {
  const location = useLocation();
  const currentPath = location.pathname;
  
  // States for Popups
  const [showAddFund, setShowAddFund] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  
  // Data for Withdraw checks
  const [totalProfit, setTotalProfit] = useState(0);
  const [totalLoss, setTotalLoss] = useState(0);
  const [userData, setUserData] = useState<any>(null);

  const fetchUserDataAndHistory = () => {
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    if (!storedUser.id) return;

    // Fetch Live User Profile
    // fetch(`http://localhost:10001/api/profile/${storedUser.id}`)
    fetch(`https://motilalalgo.com/api/profile/${storedUser.id}`)
      .then(res => res.json())
      .then(data => {
        if(!data.error) setUserData(data);
      });

    // Fetch Portfolio History
    // fetch(`http://localhost:10001/api/profile/${storedUser.id}/portfolio-history`)
    fetch(`https://motilalalgo.com/api/profile/${storedUser.id}/portfolio-history`)
      .then(res => res.json())
      .then(data => {
        if(Array.isArray(data)){
          const p = data.filter(h => parseFloat(h.profit_loss) > 0).reduce((acc, curr) => acc + parseFloat(curr.profit_loss), 0);
          const l = data.filter(h => parseFloat(h.profit_loss) < 0).reduce((acc, curr) => acc + Math.abs(parseFloat(curr.profit_loss)), 0);
          setTotalProfit(p);
          setTotalLoss(l);
        }
      });
  };

  useEffect(() => {
    if (showWithdraw) fetchUserDataAndHistory();
  }, [showWithdraw]);

  // condition calculations
  const balance = parseFloat(userData?.wallet_balance || 0);
  const minWithdraw = parseFloat(userData?.min_withdraw || 0);
  const maxLoss = parseFloat(userData?.max_loss || 0);
  const minProfit = parseFloat(userData?.min_profit || 0);

  const isBalanceMet = minWithdraw > 0 ? balance >= minWithdraw : true;
  const isProfitMet = minProfit > 0 ? totalProfit >= minProfit : true;
  const isLossMet = maxLoss > 0 ? totalLoss <= maxLoss : true;
  
  const canWithdraw = isBalanceMet && isProfitMet && isLossMet;

  const handleWithdrawSubmit = async () => {
    if(!withdrawAmount || parseFloat(withdrawAmount) <= 0) return Swal.fire("Error", "Enter a valid amount", "error");
    if(parseFloat(withdrawAmount) > balance) return Swal.fire("Error", "Insufficient Balance", "error");

    try {
      // const res = await fetch(`http://localhost:10001/api/profile/${userData.id}/withdraw`, {
      const res = await fetch(`https://motilalalgo.com/api/profile/${userData.id}/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: withdrawAmount })
      });
      const data = await res.json();
      if(data.success) {
        Swal.fire("Success", "Withdrawal requested. Amount will be credited to your bank account shortly.", "success");
        setShowWithdraw(false);
        setWithdrawAmount('');
      } else Swal.fire("Error", data.error, "error");
    } catch(err) {
      Swal.fire("Error", "Something went wrong", "error");
    }
  };

  const navItems = [
    { name: 'Portfolio', icon: PieChart, path: '/account/portfolio' },
    { name: 'Trading History', icon: Home, path: '/account/trad-history' },
    { name: 'Transactions', icon: ArrowRightLeft, path: '/account/transactions' },
    { name: 'Profile', icon: User, path: '/account/profile' },
    { name: 'Change Password', icon: Shield, path: '/account/security' },
  ];

  return (
    <>
      {/* Mobile Dark Overlay */}
      {isOpen && <div className="fixed inset-0 bg-[#0f172a]/60 backdrop-blur-sm z-40 md:hidden" onClick={onClose} />}

      <aside className={`fixed top-0 left-0 h-full w-[280px] bg-[#f4f7fc] md:bg-white border-r border-[#e2e8f0] md:border-[#f1f5f9] flex flex-col z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0`}>
        <div className="h-[88px] px-6 md:px-8 flex items-center justify-between border-b border-[#e2e8f0] md:border-[#f1f5f9] shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center overflow-hidden shadow-sm">
              <img src={logo} alt="Motilal Algo Logo" className="w-full h-full object-contain"/>
            </div>
            <span className="text-[18px] md:text-[20px] font-black tracking-tight text-[#0f172a]">Motilal Algo</span>
          </div>
          <button onClick={onClose} className="md:hidden p-2 text-[#64748b] hover:bg-[#e2e8f0] hover:text-[#0f172a] rounded-[12px] transition-colors">
            <X size={22} strokeWidth={2.5} />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto py-6 md:py-8 px-4 md:px-6 space-y-1 custom-scrollbar">
          <p className="text-[10px] font-black text-[#94a3b8] uppercase tracking-[0.15em] mb-4 px-3">Navigation Links</p>
          {navItems.map((item) => {
            const isActive = currentPath === item.path;
            return (
              <Link key={item.name} to={item.path} onClick={onClose} className={`flex items-center gap-4 px-4 py-3.5 rounded-[16px] font-bold transition-all ${isActive ? 'bg-[#3b82f6] text-white shadow-[0_4px_15px_rgba(59,130,246,0.3)]' : 'text-[#64748b] hover:bg-[#e2e8f0] md:hover:bg-[#f8fafc] hover:text-[#0f172a]'}`}>
                <item.icon size={20} strokeWidth={isActive ? 2.5 : 2} className={isActive ? "text-white" : "text-[#94a3b8]"} /> {item.name}
              </Link>
            );
          })}
        </div>

        <div className="p-4 md:p-5 border-t border-[#e2e8f0] md:border-[#f1f5f9] space-y-2 bg-slate-50/50">
          <button onClick={() => { setShowAddFund(true); if(window.innerWidth < 768) onClose(); }} className="flex items-center justify-center gap-3 px-4 py-3 w-full text-emerald-700 bg-emerald-100 hover:bg-emerald-200 rounded-[14px] font-black transition-colors shadow-sm">
            <PlusCircle size={20} strokeWidth={2.5} /> Add Fund
          </button>

          <button onClick={() => { setShowWithdraw(true); if(window.innerWidth < 768) onClose(); }} className="flex items-center justify-center gap-3 px-4 py-3 w-full text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-[14px] font-black transition-colors shadow-sm">
            <MinusCircle size={20} strokeWidth={2.5} /> Withdraw
          </button>

          <div className="h-px bg-slate-200 my-2"></div>

          <button onClick={() => { onLogout(); onClose(); }} className="flex items-center justify-center gap-3 px-4 py-3 w-full text-[#ef4444] hover:bg-[#fee2e2] rounded-[14px] font-bold transition-colors">
            <LogOut size={20} strokeWidth={2.5} /> Logout
          </button>
        </div>
      </aside>

      {/* WITHDRAW POPUP MODAL */}
      {showWithdraw && userData && (
        <div className="fixed inset-0 bg-[#0f172a]/70 backdrop-blur-md z-[100] flex items-center justify-center p-4 transition-opacity">
          <div className="bg-white rounded-[30px] p-6 md:p-8 w-full max-w-lg shadow-2xl relative animate-[fadeIn_0.2s_ease-out]">
            <button onClick={() => setShowWithdraw(false)} className="absolute top-4 right-4 p-2 text-[#94a3b8] hover:bg-[#f1f5f9] rounded-full transition-colors">
              <X size={20} strokeWidth={2.5} />
            </button>
            
            <h2 className="text-2xl font-black text-[#0f172a] mb-2">Withdraw Funds</h2>
            <p className="text-sm font-bold text-[#64748b] mb-6 border-b border-slate-100 pb-4">Check your eligibility before withdrawing.</p>

            <div className="space-y-3 mb-8">
              {minWithdraw > 0 && (
                <div className={`flex items-center justify-between p-3 rounded-xl border ${isBalanceMet ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                  <span className={`text-sm font-bold ${isBalanceMet ? 'text-emerald-700' : 'text-red-700'}`}>Min. Balance Required: ₹{minWithdraw}</span>
                  {isBalanceMet ? <CheckCircle2 className="text-emerald-600" size={20}/> : <XCircle className="text-red-500" size={20}/>}
                </div>
              )}
              {minProfit > 0 && (
                <div className={`flex items-center justify-between p-3 rounded-xl border ${isProfitMet ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                  <span className={`text-sm font-bold ${isProfitMet ? 'text-emerald-700' : 'text-red-700'}`}>Min. Profit Required: ₹{minProfit}</span>
                  {isProfitMet ? <CheckCircle2 className="text-emerald-600" size={20}/> : <XCircle className="text-red-500" size={20}/>}
                </div>
              )}
              {maxLoss > 0 && (
                <div className={`flex items-center justify-between p-3 rounded-xl border ${isLossMet ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
                  <span className={`text-sm font-bold ${isLossMet ? 'text-emerald-700' : 'text-red-700'}`}>Loss Must Be Below: ₹{maxLoss} (Your Loss: ₹{totalLoss})</span>
                  {isLossMet ? <CheckCircle2 className="text-emerald-600" size={20}/> : <XCircle className="text-red-500" size={20}/>}
                </div>
              )}
            </div>

            {canWithdraw ? (
              <div className="space-y-4 animate-[fadeIn_0.3s_ease-in]">
                <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-100 rounded-xl">
                  <Landmark className="text-blue-600 mt-1" size={24}/>
                  <div>
                    <p className="text-[11px] font-black text-blue-600 uppercase tracking-widest">Amount will be sent to:</p>
                    <p className="text-sm font-black text-slate-800 mt-1">A/c No: {userData.account_number || 'N/A'}</p>
                    <p className="text-sm font-bold text-slate-600">IFSC: {userData.ifsc_code || 'N/A'}</p>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Enter Amount (₹)</label>
                  <input type="number" value={withdrawAmount} onChange={e => setWithdrawAmount(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl font-black text-xl outline-none focus:border-blue-500" placeholder={`Max: ₹${balance}`} />
                </div>
                
                <button onClick={handleWithdrawSubmit} className="w-full bg-[#3b36a4] hover:bg-[#2d2780] text-white font-black py-4 rounded-xl shadow-lg transition-all">
                  Request Withdraw
                </button>
              </div>
            ) : (
              <div className="p-4 bg-orange-50 border border-orange-100 rounded-xl text-center">
                <p className="text-sm font-bold text-orange-800">You are currently not eligible to withdraw funds. Please meet the required conditions above.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ADD FUND POPUP MODAL */}
      {showAddFund && (
        <div className="fixed inset-0 bg-[#0f172a]/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 transition-opacity">
          <div className="bg-white rounded-[30px] p-8 w-full max-w-sm shadow-2xl relative animate-[fadeIn_0.2s_ease-out]">
            <button onClick={() => setShowAddFund(false)} className="absolute top-4 right-4 p-2 text-[#94a3b8] hover:bg-[#f1f5f9] rounded-full transition-colors">
              <X size={20} strokeWidth={2.5} />
            </button>
            <div className="text-center">
              <h2 className="text-2xl font-black text-[#0f172a] mb-2">Add Funds</h2>
              <p className="text-sm font-bold text-[#64748b] mb-6">Scan QR or use UPI ID to add funds</p>
              
              <div className="bg-[#f8fafc] p-4 rounded-[20px] border border-[#f1f5f9] flex justify-center mb-6">
                <img src={QR} alt="UPI QR" className="w-[180px] h-[180px] rounded-xl shadow-sm mix-blend-multiply" />
              </div>

              <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                <p className="text-[11px] font-black text-emerald-600 uppercase tracking-widest mb-1">UPI ID</p>
                <p className="text-lg font-black text-[#0f172a] tracking-wider selection:bg-emerald-200">moti.lalalgo@axl</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AccountSidebar;